// Tableau de bord du comptable : créances clients, dettes fournisseurs, trésorerie du mois.
import React, { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { comptaAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { tokens } from '../../theme';

const BUCKETS = [['non_echu', 'Non échu', '#5B6675'], ['j0_30', '1 à 30 j de retard', '#B7791F'], ['j31_60', '31 à 60 j', '#C2410C'], ['plus_60', 'Plus de 60 j', '#B4232A']];

const Aging = ({ title, data, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%', cursor: 'pointer' }} onClick={onClick}>
    <CardContent>
      <Typography color="text.secondary">{title}</Typography>
      <Typography sx={{ fontFamily: tokens.mono, fontSize: 28, fontWeight: 600, my: 0.5 }}>{fcfa(data.total)} F</Typography>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: '#ECEEF1', my: 1.5 }}>
        {BUCKETS.map(([k, , c]) => data[k] > 0 && <Box key={k} sx={{ width: `${(data[k] / data.total) * 100}%`, bgcolor: c }} />)}
      </Box>
      {BUCKETS.map(([k, l, c]) => (
        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, py: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />{l}</Box>
          <Box sx={{ fontFamily: tokens.mono }}>{fcfa(data[k])}</Box>
        </Box>
      ))}
    </CardContent>
  </Card>
);

const Echeances = ({ title, rows, onRow }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ pb: '8px !important' }}><Typography variant="h6">{title}</Typography></CardContent>
    <Table size="small">
      <TableHead><TableRow><TableCell>Échéance</TableCell><TableCell>Tiers</TableCell><TableCell>Pièce</TableCell><TableCell align="right">Reste</TableCell></TableRow></TableHead>
      <TableBody>
        {rows.map((r) => {
          const late = r.date_echeance < new Date().toISOString().slice(0, 10);
          return (
            <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => onRow(r)}>
              <TableCell sx={{ color: late ? 'error.main' : undefined, whiteSpace: 'nowrap' }}>{dateFr(r.date_echeance)}</TableCell>
              <TableCell>{r.tiers}</TableCell>
              <TableCell sx={{ fontFamily: tokens.mono, fontSize: 12.5 }}>{r.numero}</TableCell>
              <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{fcfa(r.reste)}</TableCell>
            </TableRow>
          );
        })}
        {rows.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>Rien dans les 30 prochains jours</TableCell></TableRow>}
      </TableBody>
    </Table>
  </Card>
);

const ComptaSituation = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { comptaAPI.situation().then((r) => setD(r.data)).catch((e) => setError(apiError(e))); }, []);
  if (!d) return <Layout title="Situation">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const t = d.tresorerie_mois;
  const mois = new Date(`${d.mois}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return (
    <Layout title="Situation financière" plain>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><Aging title="Créances clients (à encaisser)" data={d.creances} onClick={() => navigate('/factures')} /></Grid>
        <Grid item xs={12} md={6}><Aging title="Dettes fournisseurs (à payer)" data={d.dettes} onClick={() => navigate('/compta/factures-fournisseurs')} /></Grid>
        <Grid item xs={12}>
          <Card variant="outlined"><CardContent>
            <Typography color="text.secondary" sx={{ mb: 1 }}>Mouvements de trésorerie — {mois}</Typography>
            <Grid container spacing={2}>
              {[['Encaissements clients', t.encaissements, '#1B7A46', '+'], ['Paiements fournisseurs', t.paiements_fournisseurs, '#B4232A', '−'],
                ...(t.salaires === null ? [] : [['Salaires payés', t.salaires, '#B4232A', '−']]),
                [t.solde_hors_salaires ? 'Solde du mois (hors salaires)' : 'Solde du mois', t.solde, t.solde >= 0 ? '#1B7A46' : '#B4232A', t.solde < 0 ? '−' : '']].map(([l, v, c, s]) => (
                <Grid item xs={6} md={3} key={l}>
                  <Typography variant="body2" color="text.secondary">{l}</Typography>
                  <Typography sx={{ fontFamily: tokens.mono, fontSize: 22, fontWeight: 600, color: c }}>{s}{fcfa(Math.abs(v))} F</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}><Echeances title="À encaisser sous 30 jours" rows={d.a_encaisser} onRow={(r) => navigate(`/factures/${r.id}`)} /></Grid>
        <Grid item xs={12} md={6}><Echeances title="À payer sous 30 jours" rows={d.a_payer} onRow={(r) => navigate(`/compta/factures-fournisseurs?id=${r.id}`)} /></Grid>
      </Grid>
    </Layout>
  );
};

export default ComptaSituation;
